import { z } from 'zod';

export const ClickUpIdSchema = z.string().regex(/^[a-zA-Z0-9_-]+$/, 'Expected a ClickUp ID');
const nativeId = z.union([ClickUpIdSchema, z.number().int().nonnegative().safe()]).transform(String);
const timestamp = z.string().regex(/^\d+$/).refine(value => Number.isFinite(new Date(Number(value)).getTime()), 'Invalid ClickUp timestamp');
export const ClickUpUserSchema = z.object({
  id: nativeId, username: z.string().nullish(), email: z.string().email().nullish(),
  role: z.number().int().nullish(), role_key: z.string().nullish(),
  custom_role: z.object({ id: nativeId, name: z.string() }).nullish(),
  last_active: timestamp.nullish(), date_joined: timestamp.nullish(), date_invited: timestamp.nullish(),
});
export const ClickUpWorkspaceSchema = z.object({
  id: nativeId, name: z.string(), members: z.array(z.object({ user: ClickUpUserSchema })),
});
export const ClickUpListSchema = z.object({
  id: nativeId, name: z.string(), space: z.object({ id: nativeId, name: z.string().optional() }),
  folder: z.object({ id: nativeId, name: z.string().optional() }).optional(),
});
export const ClickUpScopeSchema = z.object({ workspaceId: ClickUpIdSchema, lists: z.array(ClickUpListSchema).min(1).max(100) });
export const ClickUpCommentSchema = z.object({
  id: nativeId, date: timestamp, comment_text: z.string().optional(),
  user: ClickUpUserSchema.nullish(),
  reply_count: z.union([z.number(), z.string().regex(/^\d+$/).transform(Number)]).pipe(z.number().int().nonnegative().safe()).optional(),
});
export const ClickUpTaskSchema = z.object({
  id: ClickUpIdSchema, name: z.string(), description: z.string().nullish(),
  status: z.object({ status: z.string(), type: z.string().optional() }),
  date_created: timestamp, date_updated: timestamp,
  assignees: z.array(ClickUpUserSchema), creator: ClickUpUserSchema.optional(),
  list: z.object({ id: nativeId, name: z.string() }),
  parent: ClickUpIdSchema.nullish(),
});
export const ClickUpCollectedTaskSchema = ClickUpTaskSchema.extend({
  workspaceId: ClickUpIdSchema, comments: z.array(ClickUpCommentSchema),
});
export const ClickUpRoleSchema = z.object({
  id: nativeId, team_id: nativeId, name: z.string(), inherited_role: z.number().int(),
  members: z.array(nativeId),
});
export const ClickUpMemberSchema = ClickUpUserSchema.extend({
  workspaceId: ClickUpIdSchema, workspaceName: z.string(),
});
export const CLICKUP_ROLE_NAMES: Record<number, string> = { 1: 'Owner', 2: 'Admin', 3: 'Member', 4: 'Guest' };
export type ClickUpScope = z.infer<typeof ClickUpScopeSchema>;
export type ClickUpCollectedTask = z.infer<typeof ClickUpCollectedTaskSchema>;
export type ClickUpMember = z.infer<typeof ClickUpMemberSchema>;

export const ClickUpRoleAssignmentSchema = z.object({
  user: ClickUpMemberSchema, roleId: z.string(), roleName: z.string(),
  roleType: z.enum(['builtin', 'custom']), inheritedRole: z.number().nullable(),
});
export interface ClickUpAccessRecord {
  id: string; type: string; collectedAt: string; name: string; detail: string;
}

export const ClickUpGroupSchema = z.object({
  id: ClickUpIdSchema, team_id: nativeId, name: z.string(), members: z.array(ClickUpUserSchema),
  membersFetchedAt: z.string().datetime(),
});
